import TimezoneButton from './TimezoneButton';
import type { ITheme } from '@fluentui/style-utilities';
import { ThemeProvider } from '@fluentui/react/lib/utilities/ThemeProvider';
import type { IButtonStyles } from '@fluentui/react/lib/Button';
import { Icon } from '@fluentui/react/lib/Icon';
import { observer } from 'mobx-react-lite';
import { updateAllDay, setRecurrence } from 'owa-calendar-compose-form-actions';
import { lazyRenderSpeedyMeetingsTeachingUI } from 'owa-calendar-compose-form-utils';
import SmartDateSelector from 'owa-calendar-compose-form-view/lib/components/SmartDateSelector';
import AllDayToggle from 'owa-calendar-forms-common/lib/components/AllDayToggle';
import { TimeChangeActionType } from 'owa-calendar-helpers-types/lib/TimeChangeActionType';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import type { ColorScheme } from 'owa-color-utils/lib/ColorScheme';
import { ControlIcons } from 'owa-control-icons';
import * as React from 'react';
import { getIsNarrowFullCompose } from '../selectors/getIsNarrowFullCompose';
import { getFullComposeViewState } from 'owa-calendar-compose-store-manager';
import isInEditSeriesInstanceMode from 'owa-calendar-compose-form-store/lib/utils/isInEditSeriesInstanceMode';
import isNewEvent from 'owa-calendar-compose-form-store/lib/selectors/isNewEvent';
import RecurrenceComponent from './RecurrenceComponent';
import { getRecurrenceSummary } from '../utils/getRecurrenceSummary';
import isRecurrenceSelectorInEditEnabled from 'owa-recurrence/lib/utils/isRecurrenceSelectorInEditEnabled';
import type RecurrenceType from 'owa-service/lib/contract/RecurrenceType';

import styles from './FullComposeLeftPane.scss';
import classnamesBind from 'classnames/bind';
const classNames = classnamesBind.bind(styles);

export interface TimeRowProps {
    event: CalendarEvent;
    calendarTheme: ITheme;
    buttonStyle: IButtonStyles;
    calendarColorScheme: ColorScheme;
    formId: string;
    targetWindow: Window;
}

export default observer(function TimeRow(props: TimeRowProps) {
    const { event, calendarTheme, buttonStyle, calendarColorScheme, formId, targetWindow } = props;
    const fullComposeViewState = getFullComposeViewState(formId);
    const isNarrow = getIsNarrowFullCompose();
    const renderSpeedyMeetingsTeachingUI = lazyRenderSpeedyMeetingsTeachingUI.tryImportForRender();
    const renderAfterDateRow = renderSpeedyMeetingsTeachingUI
        ? target =>
              renderSpeedyMeetingsTeachingUI(
                  target,
                  fullComposeViewState.shouldAdjustStartTimeEndTime
              )
        : undefined;

    const onSetRecurrence = React.useCallback(
        (recurrenceType: RecurrenceType) => {
            setRecurrence(recurrenceType, formId);
        },
        [formId]
    );

    const onAllDayToggled = React.useCallback(
        (isChecked: boolean) => {
            updateAllDay(isChecked, true /* isAllDayEventExplicitlySet */, formId);
        },
        [formId]
    );

    const showTzButtonAfterAllDayToggle = isRecurrenceSelectorInEditEnabled();
    const renderTzButton = !event.IsAllDayEvent && (
        <ThemeProvider theme={calendarTheme}>
            <TimezoneButton buttonStyle={buttonStyle} formId={formId} targetWindow={targetWindow} />
        </ThemeProvider>
    );

    const addAllDayToggleLeftMargin =
        !isNarrow && (event.IsAllDayEvent || isRecurrenceSelectorInEditEnabled());
    const rightSideClassName = isNarrow
        ? styles.dynamicRightSideTimeRow
        : styles.rightSideTimeColumn;

    const shouldShowRecurrenceComponent =
        !isInEditSeriesInstanceMode(event) && isRecurrenceSelectorInEditEnabled();
    const recurrenceSummaryString = getRecurrenceSummary(event);
    const shouldShowRecurrenceSummary =
        isRecurrenceSelectorInEditEnabled() && !!recurrenceSummaryString;

    return (
        <div className={styles.timeRowContainer}>
            <div className={isNarrow ? styles.dynamicTimeRow : styles.timeRow}>
                <div className={styles.leftSideTimeRow}>
                    <Icon iconName={ControlIcons.Clock} className={styles.icon} />
                    <SmartDateSelector
                        className={styles.dateSelector}
                        isNarrow={isNarrow}
                        showTimeZones={
                            !event.IsAllDayEvent && fullComposeViewState.showDateSelectorTimeZones
                        }
                        timeChangeActionType={TimeChangeActionType.FullComposeTimeControls}
                        disabled={!fullComposeViewState.uiEnabled}
                        renderAfterDateRow={renderAfterDateRow}
                        formId={formId}
                    />
                </div>
                <div className={rightSideClassName}>
                    <div className={styles.rightSideTimeRow}>
                        {!showTzButtonAfterAllDayToggle && renderTzButton}
                        {!fullComposeViewState.showDateSelectorTimeZones && (
                            <AllDayToggle
                                isAllDay={event.IsAllDayEvent}
                                onAllDayToggled={onAllDayToggled}
                                colorScheme={calendarColorScheme}
                                className={classNames(styles.allDayControl, {
                                    addLeftMargin: addAllDayToggleLeftMargin,
                                    addRightMargin: isRecurrenceSelectorInEditEnabled(),
                                })}
                                isDarkMode={fullComposeViewState.isDarkMode}
                                disabled={!fullComposeViewState.uiEnabled}
                            />
                        )}
                        {showTzButtonAfterAllDayToggle && renderTzButton}
                    </div>
                    {shouldShowRecurrenceComponent && (
                        <div
                            className={classNames(styles.recurrenceContainer, {
                                isNarrow: isNarrow,
                            })}>
                            <Icon
                                iconName={ControlIcons.SyncOccurence}
                                className={classNames(
                                    styles.recurrenceIcon,
                                    styles.recurrenceIconPadding
                                )}
                            />
                            <RecurrenceComponent
                                colorScheme={calendarColorScheme}
                                className={styles.recurrenceComponent}
                                event={event}
                                onRecurrenceChangedCallback={onSetRecurrence}
                                isNewEvent={isNewEvent(formId)}
                                formId={formId}
                                disabled={!fullComposeViewState.uiEnabled}
                                targetWindow={targetWindow}
                            />
                        </div>
                    )}
                </div>
            </div>
            {shouldShowRecurrenceSummary && (
                <div className={classNames(styles.recurrenceSummaryText)}>
                    {recurrenceSummaryString}
                </div>
            )}
        </div>
    );
});
